<template>
  <main class="resources-page">
    <header class="resources-intro">
      <div>
        <h1>{{ $t('resources_page.title') }}</h1>
        <p>{{ $t('resources_page.intro') }}</p>
      </div>
      <p class="resources-count" aria-live="polite">
        <strong>{{ filteredResources.length }}</strong>
        <span>{{ $t('resources_page.count_unit') }}</span>
      </p>
    </header>

    <nav class="resource-filters" :aria-label="$t('resources_page.title')">
      <button
        v-for="category in categories"
        :key="category.value || 'all'"
        type="button"
        :aria-pressed="activeCategory === category.value ? 'true' : 'false'"
        @click="activeCategory = category.value"
      >
        <span>{{ category.label }}</span>
        <b>{{ category.count }}</b>
      </button>
    </nav>

    <section v-if="filteredResources.length" class="resource-grid" aria-live="polite">
      <article v-for="resource in filteredResources" :key="resource.slug" class="resource-card">
        <router-link
          :to="`/resources/${resource.slug}`"
          class="resource-card__link"
          :aria-label="`${$t('resources_page.view_detail')}：${resource.title}`"
        >
          <span class="resource-card__media" :class="{'resource-card__media--mark': !resource.image}">
            <img
              v-if="resource.image"
              :src="imageFor(resource)"
              :alt="resource.title"
              width="560"
              height="360"
              loading="lazy"
            >
            <span v-else>{{ resource.logoText }}</span>
          </span>
          <span class="resource-card__body">
            <span class="resource-card__category">{{ resource.category }}</span>
            <h2>{{ resource.title }}</h2>
            <span class="resource-card__summary">{{ resource.summary }}</span>
            <span class="resource-card__action">
              {{ $t('resources_page.view_detail') }}
              <span aria-hidden="true">→</span>
            </span>
          </span>
        </router-link>
      </article>
    </section>

    <p v-else class="resource-empty">{{ $t('resources_page.empty') }}</p>
  </main>
</template>

<script>
import { resources } from '@/data/resources';

export default {
  name: 'Resources',
  data() {
    return {
      activeCategory: '',
      resources
    };
  },
  computed: {
    categories() {
      const names = ['音乐', '教育', '其他原子能力'];
      return [
        { value: '', label: this.$t('resources_page.all'), count: this.resources.length },
        ...names.map(name => ({
          value: name,
          label: name,
          count: this.resources.filter(resource => resource.category === name).length
        }))
      ];
    },
    filteredResources() {
      if (!this.activeCategory) return this.resources;
      return this.resources.filter(resource => resource.category === this.activeCategory);
    }
  },
  methods: {
    imageFor(resource) {
      return require(`@/assets/images/resources/${resource.image}`);
    }
  },
  mounted() {
    document.title = `${this.$t('resources_page.title')} - Mltcloud`;
  }
};
</script>

<style lang="stylus" scoped>
/* Hallmark · macrostructure: Catalogue · theme: Cobalt · tone: professional and restrained
 * audience: smart-hardware partners · enrichment: source artwork + typographic marks
 * pre-emit critique: P5 H5 E4 S5 R5 V4 · contrast: pass (40–41)
 * nav/footer: existing project chrome · honest: pass (46) · chrome: pass (47)
 * tokens: pass (48) · responsive/mobile: pass (34, 49–57) · icons: pass (30)
 */
.resources-page
  min-width 0
  padding var(--space-2xl) clamp(var(--space-md), 5vw, var(--space-2xl)) var(--space-3xl)
  color var(--color-ink)
  background var(--color-paper)
  font-family var(--font-body)

.resources-intro
  max-width 84rem
  margin-inline auto
  padding-block-end var(--space-xl)
  display grid
  gap var(--space-xl)
  border-bottom var(--rule-hair) solid var(--color-rule)

  h1
    min-width 0
    margin 0
    max-width 14ch
    overflow-wrap anywhere
    color var(--color-ink)
    font-family var(--font-display)
    font-size var(--text-display)
    font-style normal
    font-weight 700
    line-height 1.08
    letter-spacing -0.035em

  > div > p
    max-width 46rem
    margin var(--space-md) 0 0
    color var(--color-ink-2)
    font-size var(--text-md)
    line-height 1.65

.resources-count
  margin 0
  display flex
  align-items baseline
  gap var(--space-sm)
  color var(--color-muted)
  font-family var(--font-mono)
  font-size var(--text-sm)
  font-variant-numeric tabular-nums

  strong
    color var(--color-accent-dark)
    font-family var(--font-display)
    font-size var(--text-2xl)
    font-weight 700

.resource-filters
  max-width 84rem
  margin-inline auto
  padding-block var(--space-lg) var(--space-xl)
  display flex
  flex-wrap wrap
  gap var(--space-xs)

  button
    min-height 44px
    padding var(--space-xs) var(--space-md)
    display inline-flex
    align-items center
    gap var(--space-sm)
    color var(--color-ink-2)
    background var(--color-paper)
    border var(--rule-hair) solid var(--color-rule-strong)
    border-radius var(--radius-control)
    font-family var(--font-body)
    font-size var(--text-sm)
    white-space nowrap
    cursor pointer
    transition transform var(--dur-micro) var(--ease-out), background-color var(--dur-short) var(--ease-out), border-color var(--dur-short) var(--ease-out)

    b
      color var(--color-muted)
      font-family var(--font-mono)
      font-size var(--text-xs)
      font-variant-numeric tabular-nums

    &:hover
      border-color var(--color-accent)

    &:active
      transform translateY(1px)

    &:focus-visible
      outline 3px solid var(--color-focus)
      outline-offset 2px

    &:disabled
      color var(--color-muted)
      background var(--color-paper-2)
      cursor not-allowed

    &[aria-pressed='true']
      color var(--color-accent-ink)
      background var(--color-accent)
      border-color var(--color-accent)

      b
        color var(--color-accent-ink)

.resource-grid
  max-width 84rem
  margin-inline auto
  display grid
  grid-template-columns minmax(0, 1fr)
  gap var(--space-xl) var(--space-lg)

.resource-card
  min-width 0
  border-top var(--rule-hair) solid var(--color-rule)

.resource-card__link
  height 100%
  padding-block-start var(--space-sm)
  display grid
  grid-template-rows auto 1fr
  gap var(--space-md)
  color inherit
  text-decoration none

  &:focus-visible
    outline 3px solid var(--color-focus)
    outline-offset 4px

  &:active .resource-card__media
    transform translateY(1px)

.resource-card__media
  min-width 0
  aspect-ratio 14 / 9
  display grid
  place-items center
  overflow hidden
  color var(--color-accent-dark)
  background var(--color-paper-2)
  border var(--rule-hair) solid var(--color-rule)
  border-radius var(--radius-media)
  transition transform var(--dur-short) var(--ease-out), border-color var(--dur-short) var(--ease-out)

  img
    display block
    width 100%
    height 100%
    object-fit cover
    transition transform var(--dur-long) var(--ease-out)

  > span
    max-width 10ch
    padding var(--space-lg)
    overflow-wrap anywhere
    text-align center
    font-family var(--font-display)
    font-size var(--text-xl)
    font-weight 700
    line-height 1.12

.resource-card__body
  min-width 0
  display flex
  flex-direction column
  align-items flex-start
  gap var(--space-sm)

  h2
    min-width 0
    margin 0
    overflow-wrap anywhere
    color var(--color-ink)
    font-family var(--font-display)
    font-size var(--text-md)
    font-style normal
    font-weight 700
    line-height 1.3
    letter-spacing -0.015em

.resource-card__category
  color var(--color-accent-dark)
  font-family var(--font-mono)
  font-size var(--text-xs)
  letter-spacing 0.06em

.resource-card__summary
  display -webkit-box
  overflow hidden
  color var(--color-ink-2)
  font-size var(--text-base)
  line-height 1.65
  -webkit-box-orient vertical
  -webkit-line-clamp 4

.resource-card__action
  margin-block-start auto
  padding-block-start var(--space-xs)
  color var(--color-accent-dark)
  border-bottom var(--rule-hair) solid var(--color-rule-strong)
  font-size var(--text-sm)
  font-weight 700
  white-space nowrap

.resource-empty
  max-width 84rem
  min-height 16rem
  margin-inline auto
  color var(--color-muted)
  font-size var(--text-md)

@media (hover: hover) and (pointer: fine)
  .resource-card__link:hover .resource-card__media
    border-color var(--color-accent)

  .resource-card__link:hover .resource-card__media img
    transform scale(1.02)

@media (min-width: 40rem)
  .resources-page
    padding-block-start var(--space-3xl)

  .resources-intro
    grid-template-columns minmax(0, 1fr) auto
    align-items end

  .resource-grid
    grid-template-columns repeat(2, minmax(0, 1fr))

@media (min-width: 60rem)
  .resource-grid
    grid-template-columns repeat(3, minmax(0, 1fr))

@media (min-width: 90rem)
  .resource-grid
    grid-template-columns repeat(4, minmax(0, 1fr))

@media (prefers-reduced-motion: reduce)
  .resource-filters button,
  .resource-card__media,
  .resource-card__media img
    transition-duration 0ms
</style>
